<script>
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import Grid from '$lib/Components/Layout/Grid/Grid.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';

	import ImageCard from './ImageCard.svelte';
	import smallBundlesImg from './images/LP-Receiving-_-Handling-Guide-01.png';
	import regularSizeToteImg from './images/LP-Receiving-_-Handling-Guide-02.png';
	import crateFreightImg from './images/LP-Receiving-_-Handling-Guide-03.png';
	import step1UnpackingRegularSizeToteImg from './images/LP-Receiving-_-Handling-Guide-04.jpg';
	import step1ToteInteriorImg from './images/LP-Receiving-_-Handling-Guide-05.jpg';
	import manHoldingIndowInsertVerticallyImg from './images/LP-Receiving-_-Handling-Guide-07.jpg';
	import manHoldingIndowInsertGripOnEitherSideImg from './images/LP-Receiving-_-Handling-Guide-08.jpg';
	import manCarryingIndowInsertAboveHisHeadImg from './images/LP-Receiving-_-Handling-Guide-09.jpg';
	import manHoldingIndowInsertCompressionTubeImg from './images/LP-Receiving-_-Handling-Guide-10.jpg';
	import damagedIndowInsertImg from './images/LP-Receiving-_-Handling-Guide-11.jpg';
	import proceedWithInstructionForInstallationGuideImg from './images/LP-Receiving-_-Handling-Guide-12.jpg';
	import scallopedTubing from './images/scallopedTubing.png';
</script>

<Section backgroundColor="blue">
	<p>
		We take every precaution to safeguard your shipment against damage, but
		damage can occur once the product has left the Indow factory. Our freight
		shipments are shipped in three configurations. (See below.) Totes are for
		bulky units or unit quantities of 1-8. Crates are used for shipments
		exceeding 8 units.
	</p>
	<Grid columns={3} rows={2} center>
		<Grid subgrid vertical>
			<Image
				src={smallBundlesImg}
				class="
      max-w-68
      md:max-w-48
    "
			/>
			<p>SMALL BUNDLES (GROUND)</p>
		</Grid>

		<Grid subgrid vertical>
			<Image
				src={regularSizeToteImg}
				class="
      max-w-68
      md:max-w-48
    "
			/>
			<p>REGULAR SIZE TOTE</p>
		</Grid>

		<Grid subgrid vertical>
			<Image
				src={crateFreightImg}
				class="
      max-w-68
      md:max-w-48
    "
			/>
			<p>CRATE (FREIGHT)</p>
		</Grid>
	</Grid>
	<p>
		As per the terms of your order, title passes to you once the shipment has
		been picked up by the carrier. <strong>
			Please do not refuse the shipment, even if you see or suspect damage
		</strong>. If the steps below are followed, Indow will manage the carrier
		freight claim and ship replacement inserts to you at no charge. Indow
		reserves the right to include the replacements in a bundled shipment.
	</p>
</Section>

<Section>
	<h2>During Inspection</h2>
	<ul>
		<li>
			Inspect the freight before you sign for it. (Ground shipments do not
			require inspection or signature).
		</li>
		<li>
			When signing for freight, write "SUBJECT TO INSPECTION" if you cannot
			inspect before signing for the delivery.
		</li>
		<li>
			Note on the freight bill any damage at the time of delivery (corners
			crushed, tears, rips, slices) in as much detail as possible.
		</li>
		<li>Note any shortages on the freight bill.</li>
		<li>
			If you suspect internal damage, open immediately while the driver is
			present. The driver cannot leave until the shipment has been signed for.
		</li>
		<li>
			Common signs of mishandling include holes in exterior side walls, crushed
			corners and damaged wooden pallets.
		</li>
		<li>Open the carton(s) within 24 hours to inspect thoroughly.</li>
		<li>Take photos of the packaging and the damaged product.</li>
	</ul>
</Section>

<Section>
	<h2>If Damage Is Found</h2>
	<ul>
		<li>
			Note the freight damage on the freight bill and contact us immediately.
		</li>
		<li>
			Hold all damaged goods and packaging materials in the original location
			for possible inspection by the carrier.
		</li>
		<li>Keep a copy of the signed freight bill.</li>
		<li>
			The carrier, at their sole discretion, may require the damaged product be
			packaged and made available for pickup.
		</li>
	</ul>
	<p>
		Please notify us at
		<a href="/claim-submission-customer">
			https://<wbr />indowwindows.<wbr />com/<wbr />claim-submission-customer</a
		>. We will follow up with you and may request additional information to
		complete the claim.
	</p>
	<Button href="/claim-submission-customer">LEARN MORE</Button>
</Section>

<Section backgroundColor="grey-light">
	<h2>STEP 1</h2>
	<Grid columns={2} rows={4} center class="gap-8">
		<Grid vertical subgrid>
			<ImageCard imgSrc={step1UnpackingRegularSizeToteImg} />
			<p class="font-bold">REGULAR SIZE TOTE</p>
			<h3>UNPACKING YOUR INDOW INSERTS FROM A CRATE</h3>
			<ol
				class="
      w-full list-[upper-alpha]
      marker:font-bold
    "
			>
				<li>
					<p class="text-left">
						Slice across brown tape on top of the crate to open its lid.
					</p>
				</li>
				<li>
					<p class="text-left">Remove the lid.</p>
				</li>
				<li>
					<p class="text-left">
						Locate the front of the crate, which will be labeled “FRONT.” Using
						needle nose pliers, pry out staples on either the right or left side
						of the front of the crate.
					</p>
				</li>
				<li>
					<p class="text-left">
						Pry the staples out of the front of the crate and bottom edge. We
						recommend using needle nose pliers.
					</p>
				</li>
				<li>
					<p class="text-left">
						Once one side of the cardboard is free, swing the larger piece of
						cardboard out like a door. Inside will be the edge-wrapped Indow
						inserts that you can slide out of the crate and unpack using box
						cutters or scissors. Be careful not to puncture the silicone edges.
					</p>
				</li>
			</ol>
		</Grid>

		<Grid vertical subgrid>
			<ImageCard imgSrc={step1ToteInteriorImg} />

			<p class="font-bold">TOTE INTERIOR</p>
			<h3>UNPACKING YOUR INDOW INSERTS FROM TOTES & BUNDLES</h3>
			<ol
				class="
      w-full list-[upper-alpha]
      marker:font-bold
    "
			>
				<li>
					<p class="text-left">
						Using a box cutter or scissors, cut all the straps surrounding the
						tote.
					</p>
				</li>
				<li>
					<p class="text-left">
						Slice across brown tape on top of the tote. The pieces of cardboard
						will come apart and you can easily swing them open to remove them.
					</p>
				</li>
				<li>
					<p class="text-left">
						Once the tote is open, cut and remove any excess straps or cardboard
						if necessary.
					</p>
				</li>
				<li>
					<p class="text-left">
						Grab the insert with both hands and lift it straight out of the
						tote. This step may be easier with two people.
					</p>
				</li>
				<p>
					Larger totes (over 92" long and/or 70" high) will be marked
					"FRONT"/"BACK." Open the tote from the "FRONT" side.
				</p>
			</ol>
		</Grid>
	</Grid>
</Section>

<Section>
	<h2>STEP 2</h2>
	<h3>FOR CRATES AND LARGER TOTES:</h3>
	<ol
		class="
    w-full list-[upper-alpha]
    marker:font-bold
  "
	>
		<li>
			<p class="text-left">Carefully cut the straps.</p>
		</li>
		<li>
			<p class="text-left">
				Cut through the packing tape along the dotted lines at the top of the
				tote.
			</p>
		</li>
		<li>
			<p class="text-left">
				Remove the striped cardboard edging and the cardboard wrapping by
				cutting through clear tape.
			</p>
		</li>
		<li>
			<p class="text-left">
				Pull away the folds of the cardboard tote or crate.
			</p>
		</li>
		<li>
			<p class="text-left">
				Remove your inserts and identitfy which window they're for and peel away
				the protective film on the window facing side.
			</p>
		</li>
		<li>
			<p class="text-left">
				Once you've installed the insert clean the the room facing side of the
				insert.
			</p>
		</li>
	</ol>
	<Image
		class="px-4"
		src="/static/routes/guides/receiving-your-inserts/LP-Receiving-_-Handling-Guide-06.gif"
	/>
</Section>

<Section backgroundColor="grey-light">
	<h2>STEP 3</h2>
	<h3>HANDLING ONCE UNPACKED</h3>
	<ol
		class="
    w-full list-[upper-alpha]
    marker:font-bold
  "
	>
		<li>
			<p class="text-left">
				Indow inserts should be held vertically to avoid bending or bowing.
			</p>
		</li>
		<li>
			<p class="text-left">
				If larger than 12 sq. ft., stagger your grip on either side of the
				panel.
			</p>
		</li>
		<li>
			<p class="text-left">
				If you can’t get your arms around the insert, two people should carry
				it.
			</p>
		</li>
	</ol>
	<Grid
		columns={2}
		rows={1}
		center
		class="
    gap-x-20
    max-md:gap-12
  "
	>
		<ImageCard imgSrc={manHoldingIndowInsertVerticallyImg} text="YES" />
		<ImageCard imgSrc={manHoldingIndowInsertGripOnEitherSideImg} text="YES" />
	</Grid>
	<p>Do not carry above your head or by holding the compression tube.</p>
	<Grid
		columns={2}
		rows={1}
		center
		class="
    gap-x-20
    max-md:gap-12
  "
	>
		<ImageCard imgSrc={manCarryingIndowInsertAboveHisHeadImg} text="NO" />
		<ImageCard imgSrc={manHoldingIndowInsertCompressionTubeImg} text="NO" />
	</Grid>
</Section>

<Section>
	<h2>STEP 4</h2>
	<h3>INSPECTION</h3>
	<div class="mb-8 max-w-lg">
		<Image src={scallopedTubing} alt="scalloped tubing" />
		<p class="text-left text-sm italic">
			Please note: Indow inserts have scalloped cutouts on their backs that
			allow for a tight seal when inserted into your windows. This will be
			obscured by your window frame and does not constitute damage.
		</p>
	</div>
	<ol
		class="
    w-full list-[upper-alpha]
    marker:font-bold
  "
	>
		<li>
			Look for:
			<ol
				class="
      list-[lower-roman]
      marker:text-indow-grey-dark
    "
			>
				<li>
					<p class="text-left">Cracks in the acrylic</p>
				</li>
				<li>
					<p class="text-left">Tears in the protective sheeting</p>
					<p class="text-left">
						Hold the insert up to a light source to detect any slits of light
						coming through.
					</p>
				</li>
				<li><p class="text-left">Tears in the compression tube</p></li>
			</ol>
		</li>
		<li>
			If any damage or defects are found, report them to Indow immediately: <a
				href="/claim-submission-customer"
				>https://<wbr />indowwindows.<wbr />com/<wbr />claim-submission-customer
			</a>
		</li>
		<li>
			If no damage is found, proceed with the instructions in our Installation
			Guide.
		</li>
	</ol>
	<Grid
		columns={2}
		rows={1}
		center
		class="
    gap-x-20
    max-md:gap-12
  "
	>
		<ImageCard imgSrc={damagedIndowInsertImg} text="YES" />
		<ImageCard
			imgSrc={proceedWithInstructionForInstallationGuideImg}
			text="NO"
		/>
	</Grid>
</Section>
